import * as Sentry from '@sentry/react'

type CustomError = Error & { title?: string }

type ExtraProperties = {
  title?: string
  section?: string
  error?: Error
} & Partial<Record<string, unknown | undefined>>

const IGNORED_SENTRY_ERROR_MESSAGES = ['Network Error', 'Request aborted']

type ErrorWithDetails = Error & {
  code?: string
  config?: { url?: string }
  response?: { status?: number }
}

// Only whitelisted fields: serialising an AxiosError whole includes the
// request body and headers (emails, phone numbers, API keys).
function formatContextError(error: ErrorWithDetails | undefined) {
  if (!error) return ''

  return {
    name: error.name,
    message: error.message,
    code: error.code,
    status: error.response?.status,
    url: error.config?.url?.split('?')[0]
  }
}

const REDACTED = '[redacted]'
const MAX_REDACT_DEPTH = 3
const SENSITIVE_KEYS = [
  'token',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'customeraccesstoken',
  'authorization',
  'password',
  'email',
  'phone',
  'apikey'
]

// Matches camelCase, snake_case and kebab-case spellings alike
// (accessToken, access_token, access-token).
function isSensitiveKey(key: string) {
  return SENSITIVE_KEYS.includes(key.toLowerCase().replace(/[_-]/g, ''))
}

// Credentials and personal details must never reach Sentry, whichever call
// site passes them.
function redactSensitive(value: unknown, depth = 0): unknown {
  if (depth >= MAX_REDACT_DEPTH) return value

  if (Array.isArray(value)) {
    return value.map(item => redactSensitive(item, depth + 1))
  }

  if (value && typeof value === 'object' && value.constructor === Object) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        isSensitiveKey(key) ? REDACTED : redactSensitive(item, depth + 1)
      ])
    )
  }

  return value
}

function findIgnoredErrors(error: Error | undefined) {
  if (!error) return false

  const errorMessage = (error?.message || error) as string

  return IGNORED_SENTRY_ERROR_MESSAGES.includes(errorMessage)
}

function trackError(error: CustomError, extra?: ExtraProperties) {
  // Can't use helper to check prod because it causes a circular dependency error
  if (process.env.API_ENV !== 'production' || !process.browser) {
    console.error(error, extra)
  }

  try {
    const sentryError = error || new Error('Track error')
    sentryError.title = extra?.title || error?.title || 'Error'
    const context = {
      ...(redactSensitive(extra) as ExtraProperties),
      error: formatContextError(extra?.error)
    }

    const isInvalidError = extra && findIgnoredErrors(extra?.error)

    if (!isInvalidError) {
      Sentry.captureException(sentryError, scope => {
        scope.clear()
        if (extra) {
          scope.setContext('extra', context)
          scope.setTag('section', extra.section)
        }
        return scope
      })
    }
  } catch (e) {
    console.error(e)
  }
}

export function trackMessage(msg: string, context?: Record<string, string>) {
  try {
    Sentry.captureMessage(msg, scope => {
      scope.clear()
      if (context) {
        scope.setContext('extra', context)
      }
      return scope
    })
  } catch (e) {
    console.error(e)
  }
}

export default trackError
